import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { PrincipalTypeSelectors } from "../../../../../components/guardian/PrincipalFormComponents";
import { getPrincipalMode } from "../../../../../components/guardian/principalHelpers";
import RetrievalPgSection from "./RetrievalPgSection";
import RetrievalAttorneySection from "./RetrievalAttorneySection";
import RetrievalManualSection from "./RetrievalManualSection";

export default function RetrievalGuardianPrincipal({
  bondKind,
  index,
  guardian,
  guardians,
  globalData,
  data,
  updateData,
  retrievalCfg: cfg,
  keys,
  role,
  isDisabled,
  pii_access,
  professionalGuardians,
  attorneys,
  loadingCitiesStates,
  loadingGuardians,
  loadingAttorneys,
  attorneySearchEnabled = false,
  attorneySearchInput = "",
  setAttorneySearchInput,
  debouncedSearchAttorneys,
  clearAttorneys,
  allCities,
  allStates,
  showSSNStates,
  toggleShowSSN,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  splitEmploymentOccupation,
  updateGuardianAt,
  fetchProfessionalGuardianDetails,
  handleProfessionalGuardianChange,
  handleAttorneyChange,
  handleAttorneySelection,
  handleAttorneyHomeAddressChange,
  handleGoogleCallback,
  handleZipCodeChange,
  handleNameChange,
  handleFieldChange,
  handleEmploymentOccupationChange,
}) {
  const mode = getPrincipalMode(guardian);

  return (
    <Box key={`guardian${index}`} className="grid grid-cols-1 gap-4">
      <Typography variant="h6" className="font-semibold mb-2">
        Principal #{index + 1}
      </Typography>

      <PrincipalTypeSelectors
        guardian={guardian}
        isDisabled={isDisabled}
        isGuest={false}
        onProfessionalGuardianChange={(value) =>
          handleProfessionalGuardianChange(index, value, guardian, guardians)
        }
        onAttorneyChange={(value) =>
          handleAttorneyChange(index, value, guardians)
        }
      />

      {mode === "pg_lookup" && (
        <RetrievalPgSection
          index={index}
          guardian={guardian}
          guardians={guardians}
          retrievalCfg={cfg}
          professionalGuardians={professionalGuardians}
          loadingGuardians={loadingGuardians}
          isDisabled={isDisabled}
          updateGuardianAt={updateGuardianAt}
          fetchProfessionalGuardianDetails={fetchProfessionalGuardianDetails}
        />
      )}

      {mode === "attorney_lookup" && (
        <RetrievalAttorneySection
          index={index}
          guardian={guardian}
          guardians={guardians}
          data={data}
          updateData={updateData}
          retrievalCfg={cfg}
          keys={keys}
          attorneys={attorneys}
          loadingAttorneys={loadingAttorneys}
          attorneySearchEnabled={attorneySearchEnabled}
          attorneySearchInput={attorneySearchInput}
          setAttorneySearchInput={setAttorneySearchInput}
          debouncedSearchAttorneys={debouncedSearchAttorneys}
          clearAttorneys={clearAttorneys}
          isDisabled={isDisabled}
          allCities={allCities}
          allStates={allStates}
          loadingCitiesStates={loadingCitiesStates}
          handleAttorneySelection={handleAttorneySelection}
          handleAttorneyHomeAddressChange={handleAttorneyHomeAddressChange}
          handleGoogleCallback={handleGoogleCallback}
          handleZipCodeChange={handleZipCodeChange}
          handleFieldChange={handleFieldChange}
          GoogleAutoComplete={GoogleAutoComplete}
          LabeledCreatableSelect={LabeledCreatableSelect}
        />
      )}

      {mode === "manual" && (
        <RetrievalManualSection
          index={index}
          guardian={guardian}
          guardians={guardians}
          globalData={globalData}
          retrievalCfg={cfg}
          keys={keys}
          role={role}
          pii_access={pii_access}
          isDisabled={isDisabled}
          allCities={allCities}
          allStates={allStates}
          loadingCitiesStates={loadingCitiesStates}
          GoogleAutoComplete={GoogleAutoComplete}
          LabeledCreatableSelect={LabeledCreatableSelect}
          showSSNStates={showSSNStates}
          toggleShowSSN={toggleShowSSN}
          handleNameChange={handleNameChange}
          handleFieldChange={handleFieldChange}
          handleZipCodeChange={handleZipCodeChange}
          handleGoogleCallback={handleGoogleCallback}
          handleEmploymentOccupationChange={handleEmploymentOccupationChange}
          splitEmploymentOccupation={splitEmploymentOccupation}
        />
      )}
    </Box>
  );
}

RetrievalGuardianPrincipal.propTypes = {
  bondKind: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  guardian: PropTypes.object.isRequired,
  guardians: PropTypes.array.isRequired,
  globalData: PropTypes.object,
  data: PropTypes.shape({ guardians: PropTypes.array }).isRequired,
  updateData: PropTypes.func.isRequired,
  retrievalCfg: PropTypes.object.isRequired,
  keys: PropTypes.object.isRequired,
  role: PropTypes.string,
  isDisabled: PropTypes.bool,
  pii_access: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  professionalGuardians: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  loadingCitiesStates: PropTypes.bool,
  loadingGuardians: PropTypes.bool,
  loadingAttorneys: PropTypes.bool,
  attorneySearchEnabled: PropTypes.bool,
  attorneySearchInput: PropTypes.string,
  setAttorneySearchInput: PropTypes.func,
  debouncedSearchAttorneys: PropTypes.func,
  clearAttorneys: PropTypes.func,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  showSSNStates: PropTypes.object.isRequired,
  toggleShowSSN: PropTypes.func.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  splitEmploymentOccupation: PropTypes.func.isRequired,
  updateGuardianAt: PropTypes.func.isRequired,
  fetchProfessionalGuardianDetails: PropTypes.func.isRequired,
  handleProfessionalGuardianChange: PropTypes.func.isRequired,
  handleAttorneyChange: PropTypes.func.isRequired,
  handleAttorneySelection: PropTypes.func.isRequired,
  handleAttorneyHomeAddressChange: PropTypes.func.isRequired,
  handleGoogleCallback: PropTypes.func.isRequired,
  handleZipCodeChange: PropTypes.func.isRequired,
  handleNameChange: PropTypes.func.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  handleEmploymentOccupationChange: PropTypes.func.isRequired,
};
